<template>
  <a-modal
      :open="open"
      :footer="null"
      :width="650"
      @cancel="handleClose"
      class="feedback-form-modal modal-no-padding"
  >
    <div class="modal-no-padding__header">
      <h2>Форма обратной связи</h2>
    </div>

    <a-divider style="margin: 16px 0" />

    <div class="modal-no-padding__body">
      <a-form
          ref="formRef"
          layout="vertical"
          :model="form"
          class="feedback-form"
      >
        <div class="feedback-form__row">
          <a-form-item
              label="Ваше ФИО"
              name="name"
              :rules="[{ required: true, message: 'Введите ФИО' }]"
              class="feedback-form__item"
          >
            <a-input
                v-model:value="form.name"
                placeholder="Иванов Иван Иванович"
            />
          </a-form-item>

          <a-form-item
              label="Почта для ответа"
              name="email"
              :rules="[
              { required: true, message: 'Введите почту' },
              { type: 'email', message: 'Введите корректную почту' }
            ]"
              class="feedback-form__item"
          >
            <a-input
                v-model:value="form.email"
                placeholder="example@mail.com"
            />
          </a-form-item>
        </div>

        <a-form-item
            label="Выберите тип"
            name="type"
            :rules="[{ required: true, message: 'Выберите тип обращения' }]"
        >
          <a-select
              v-model:value="form.type"
              placeholder="Выберите тип"
              :options="typeOptions"
          />
        </a-form-item>

        <a-form-item
            label="Комментарий"
            name="comment"
            required
            :rules="[{ required: true, message: 'Введите комментарий' }, { min: 10, message: 'Минимум 10 символов' }]"
        >
          <a-textarea
              v-model:value="form.comment"
              :rows="5"
              placeholder=""
              :maxlength="5000"
              :auto-size="{ minRows: 5 }"
          />
        </a-form-item>

        <div class="feedback-form__files-label">Файлы до 10 Мб</div>

        <a-upload
            :before-upload="beforeUpload"
            :file-list="fileList"
            :multiple="true"
            :max-count="5"
            list-type="text"
            @remove="handleRemove"
            class="feedback-form__upload"
        >
          <template #removeIcon>
            <span class="feedback-form__remove-text">Удалить</span>
          </template>

          <a-button type="link" class="feedback-form__add-file">
            <template #icon>
              <PlusOutlined />
            </template>
            Добавить
          </a-button>
        </a-upload>
      </a-form>
    </div>

    <a-divider style="margin: 16px 0" />

    <div class="modal-no-padding__footer">
      <div class="feedback-form__actions">
        <a-button @click="handleClose" class="secondary-button">
          Отменить
        </a-button>

        <a-button
            class="primary-button"
            :loading="loading"
            :disabled="isSubmitDisabled"
            @click="handleSubmit"
        >
          Отправить
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { ref, reactive, watch } from 'vue'
import { message } from 'ant-design-vue'
import { PlusOutlined } from '@ant-design/icons-vue'
import {useStore} from "vuex";
import {computed} from "@vue/reactivity";

export default {
  name: 'FeedbackForm',
  components: {
    PlusOutlined,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const formRef = ref(null);
    const loading = ref(false);
    const store = useStore();

    const defaultForm = () => ({
      name: '',
      email: '',
      type: undefined,
      comment: '',
    });

    const form = reactive(defaultForm())

    const typeOptions = ref([
      {
        value: 'Задать вопрос',
        label: 'Задать вопрос',
      },
      {
        value: 'Нашли ошибку',
        label: 'Нашли ошибку',
      },
      {
        value: 'Идеи и предложения',
        label: 'Идеи и предложения',
      },
      {
        value: 'Другое',
        label: 'Другое',
      },
    ]);

    const fileList = ref([]);

    const resetForm = () => {
      Object.assign(form, defaultForm())
      fileList.value = []
      formRef.value?.clearValidate?.();
    }

    watch(
        () => props.open,
        (value) => {
          if (!value) {
            resetForm();
          }
        }
    )

    const beforeUpload = (file) => {
      const isValidSize = file.size / 1024 / 1024 <= 10;

      if (!isValidSize) {
        message.error(`Файл "${file.name}" больше 10 Мб`)
        return false;
      }

      fileList.value = [...fileList.value, file];
      return false;
    }

    const handleRemove = (file) => {
      const targetIndex = fileList.value.findIndex(
          (item) => item.uid === file.uid
      )

      if (targetIndex !== -1) {
        fileList.value.splice(targetIndex, 1)
      }
    }

    const handleClose = () => {
      resetForm();
      store.commit('support/hideForm');
    }

    const buildFormData = () => {
      const formData = new FormData()

      formData.append('name', form.name)
      formData.append('email', form.email)
      formData.append('type', form.type)
      formData.append('comment', form.comment)

      fileList.value.forEach((file) => {
        formData.append('files[]', file)
      });

      return formData;
    }

    const handleSubmit = async () => {
      try {
        await formRef.value.validate()
        loading.value = true

        const formData = buildFormData()

        store.dispatch('support/sendForm', formData).then(() => {
          message.success('Форма успешно отправлена')
          handleClose();
        });
      } catch (error) {
        if (error?.errorFields) {
          return
        }

        message.error('Не удалось отправить форму')
      } finally {
        loading.value = false
      }
    }

    const isSubmitDisabled = computed(() => {
      return !form.name.trim()
          || !form.email.trim()
          || !form.type
          || form.comment.trim().length < 10
          || loading.value;
    });

    return {
      formRef,
      form,
      loading,
      fileList,
      typeOptions,
      beforeUpload,
      handleRemove,
      handleClose,
      handleSubmit,
      isSubmitDisabled
    }
  },
}
</script>

<style scoped lang="scss">
.feedback-form {
  padding-bottom: 8px;


  :deep(.ant-form-item-label) {
    padding-bottom: 4px;
  }
}

.feedback-form__row {
  display: flex;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
}

.feedback-form__item {
  flex: 1;
}

.feedback-form__upload {
  margin-bottom: 24px;
}

:deep(.feedback-form__upload.ant-upload-wrapper) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

:deep(.feedback-form__upload .ant-upload-list) {
  order: 1;
  width: 100%;
}

:deep(.feedback-form__upload .ant-upload-select) {
  order: 2;
}

:deep(.feedback-form__upload .ant-upload-list-item-container) {
  width: 100%;
  margin-bottom: 8px;
}

:deep(.feedback-form__upload .ant-upload-list-item) {
  display: inline-flex !important;
  align-items: center;
  width: auto !important;
  max-width: 100%;
  gap: 8px;
  padding: 0 !important;
  margin: 0 !important;
  border: 0 !important;
  background: transparent !important;
}

:deep(.feedback-form__upload .ant-upload-list-item-name) {
  flex: 0 1 auto !important;
  width: auto !important;
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 6px;
  padding: 0 !important;
  color: var(--main-green) !important;
}

:deep(.feedback-form__upload .ant-upload-list-item-actions) {
  display: inline-flex !important;
  align-items: center;
  flex: 0 0 auto !important;
  margin-left: 0 !important;
  opacity: 1 !important;
  visibility: visible !important;
}

:deep(.feedback-form__upload .ant-upload-list-item-action) {
  margin-inline-start: 0 !important;
  padding: 0 !important;
}

.feedback-form__remove-text {
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
}

.feedback-form__files-label {
  margin-bottom: 8px;
}

:deep(.feedback-form__upload .ant-upload-list-item-actions) {
  opacity: 1 !important;
  visibility: visible !important;
}

:deep(.feedback-form__upload .ant-upload-list-item-action) {
  opacity: 1 !important;
  visibility: visible !important;
}

:deep(.ant-upload-list-item-action:hover) {
  background: unset !important;
}

.feedback-form__remove-text {
  color: rgba(0, 0, 0, 0.45);
  line-height: 1;
}

.feedback-form__add-file {
  font-size: 14px !important;
  padding: 0 !important;
  color: var(--main-green) !important;
}

.feedback-form__add-file:hover,
.feedback-form__add-file:focus,
.feedback-form__add-file:active {
  color: var(--main-green) !important;
}

.feedback-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  @media (max-width: 768px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
}

.submit-button {
  background-color: var(--main-green) !important;
  border-color: var(--main-green) !important;
  color: #fff !important;
}

.submit-button:hover,
.submit-button:focus,
.submit-button:active {
  background-color: var(--main-green) !important;
  border-color: var(--polar-green-6) !important;
  color: #fff !important;
}

:deep(.ant-modal-content) {
  border-radius: 8px;
}

:deep(.ant-form-item) {
  margin-bottom: 12px;
}

:deep(.ant-input:hover),
:deep(.ant-input:focus),
:deep(.ant-input-affix-wrapper:hover),
:deep(.ant-input-affix-wrapper-focused),
:deep(.ant-select:hover .ant-select-selector),
:deep(.ant-select-focused .ant-select-selector),
:deep(.ant-input-textarea:hover),
:deep(.ant-input-textarea-focused) {
  border-color: var(--main-green) !important;
}

:deep(.ant-input:focus),
:deep(.ant-input-affix-wrapper-focused),
:deep(.ant-select-focused .ant-select-selector),
:deep(.ant-input-textarea-focused textarea) {
  box-shadow: 0 0 0 2px var(--polar-green) !important;
}

h2 {
  margin: 0;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
}
</style>
